import React from 'react';
import { Table, Button, Tag, Space, Popconfirm } from 'antd';
import { StopOutlined, ReloadOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { Admin } from '../types/admin.types';

interface AdminsTableProps {
    admins: Admin[];
    loading: boolean;
    isSuperAdmin: boolean;
    onEdit: (admin: Admin) => void;
    onDisable: (id: string) => void;
    onEnable: (id: string) => void;
    onDelete: (id: string) => void;
    onResetMfa: (admin: Admin) => void;
}

export const AdminsTable: React.FC<AdminsTableProps> = ({
    admins,
    loading,
    isSuperAdmin,
    onEdit,
    onDisable,
    onEnable,
    onDelete,
    onResetMfa
}) => {
    const columns: ColumnsType<Admin> = [
        {
            title: 'Name/Email',
            dataIndex: 'email',
            key: 'email',
            render: (email: string, record) => (
                <div>
                    {record.name && <div><strong>{record.name}</strong></div>}
                    <div style={{ color: '#888' }}>{email}</div>
                </div>
            )
        },
        {
            title: 'Roles',
            dataIndex: 'roles',
            key: 'roles',
            render: (roles: any[]) => (
                <>
                    {roles.map(r => (
                        <Tag color={r.name === 'SUPER_ADMIN' || r.slug === 'super_admin' ? 'red' : 'blue'} key={r.id}>
                            {r.name.toUpperCase()}
                        </Tag>
                    ))}
                </>
            )
        },
        {
            title: 'MFA Status',
            dataIndex: 'mfa_enabled',
            key: 'mfa',
            render: (enabled: boolean) => (
                <Tag color={enabled ? 'green' : 'orange'}>
                    {enabled ? 'ENABLED' : 'NOT SETUP'}
                </Tag>
            )
        },
        {
            title: 'Status',
            dataIndex: 'is_active',
            key: 'status',
            render: (active: boolean) => (
                <Tag color={active ? 'green' : 'red'}>
                    {active ? 'ACTIVE' : 'DISABLED'}
                </Tag>
            )
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: Admin) => (
                <Space>
                    {isSuperAdmin && (
                        <Button
                            size="small"
                            onClick={() => onEdit(record)}
                        >
                            Edit
                        </Button>
                    )}

                    {isSuperAdmin && record.is_active && (
                        <Popconfirm
                            title="Disable Admin?"
                            description="This will instantly revoke access."
                            onConfirm={() => onDisable(record.id)}
                        >
                            <Button danger size="small" icon={<StopOutlined />}>Disable</Button>
                        </Popconfirm>
                    )}

                    {isSuperAdmin && !record.is_active && (
                        <Popconfirm
                            title="Enable Admin?"
                            description="This will restore access."
                            onConfirm={() => onEnable(record.id)}
                        >
                            <Button type="primary" size="small">Enable</Button>
                        </Popconfirm>
                    )}

                    {isSuperAdmin && (
                        <Button
                            size="small"
                            icon={<ReloadOutlined />}
                            onClick={() => onResetMfa(record)}
                        >
                            Reset MFA
                        </Button>
                    )}

                    {isSuperAdmin && (
                        <Popconfirm
                            title="Delete Admin?"
                            description="This will permanently revoke access and hide the admin."
                            onConfirm={() => onDelete(record.id)}
                        >
                            <Button danger size="small" icon={<DeleteOutlined />}>Delete</Button>
                        </Popconfirm>
                    )}
                </Space>
            )
        }
    ];

    return (
        <Table
            dataSource={admins}
            columns={columns}
            rowKey="id"
            loading={loading}
            pagination={{
                total: admins.length,
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} admins`,
            }}
        />
    );
};
